import { AiRan5GStatus, AiRanTelemetry, AiRanTelemetryDto, AvgDeltaTime, Cn5gStatus, MachineId, OaiboxStatus, StompService, TenantId } from '@allbesmart/web-app-common';
import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { filter, map, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WS_SUFFIX } from '../external/ws';

@Injectable({
  providedIn: 'root'
})
export class AiRanTelemetryService {
  private aiRanTelemetries: Map<MachineId, AiRanTelemetryDto> = new Map();
  private aiRanTelemetriesSubscriptions: Map<MachineId, Subscription> = new Map();
  private aiRanTelemetriesStatusTimeout: Map<MachineId, ReturnType<typeof setTimeout>> = new Map();
  private avgTelemetriesDeltaTimesMs: Map<MachineId, AvgDeltaTime> = new Map();

  private aiRanTelemetryChanges: EventEmitter<MachineId> = new EventEmitter();

  constructor(
    private stompService: StompService,
    private ngZone: NgZone,
  ) {
  }

  public listenToAiRanTelemetry(tenantId: TenantId, machineId: MachineId): void {
    if (!this.aiRanTelemetriesSubscriptions.has(machineId)) {
      this.aiRanTelemetriesSubscriptions.set(machineId, this.stompService
        .subscribeTopic<AiRanTelemetryDto>(`${tenantId}.${machineId}.${WS_SUFFIX.AI_RAN_TELEMETRY}`)
        .subscribe(freshAiRanTelemetry => {
          this.ngZone.runOutsideAngular(() => {
            freshAiRanTelemetry.status = OaiboxStatus.ONLINE;

            let avgDeltaTime = this.avgTelemetriesDeltaTimesMs.get(machineId);
            if (!avgDeltaTime) this.avgTelemetriesDeltaTimesMs
              .set(machineId, avgDeltaTime = new AvgDeltaTime(environment.oaiboxTelemetryExpiryMs))

            const aiRanTelemetryOptional = this.aiRanTelemetries.get(machineId);
            if (aiRanTelemetryOptional) {
              clearTimeout(this.aiRanTelemetriesStatusTimeout.get(machineId));
              this.updateAiRan(aiRanTelemetryOptional, freshAiRanTelemetry);
            } else {
              this.aiRanTelemetries.set(machineId, freshAiRanTelemetry);
            }

            this.aiRanTelemetriesStatusTimeout.set(machineId, setTimeout(() => {
              const oaiboxTelemetry = this.aiRanTelemetries.get(machineId);
              if(oaiboxTelemetry){
                oaiboxTelemetry.status = OaiboxStatus.OFFLINE;
                oaiboxTelemetry.cn5gStatus = Cn5gStatus.STOPPED;
                if (oaiboxTelemetry.gnbs) {
                  oaiboxTelemetry.gnbs.forEach(gnb => {
                    if (gnb.component) gnb.component.status = AiRan5GStatus.STOPPED
                  });
                }
              }
              this.aiRanTelemetryChanges.emit(machineId);
            }, avgDeltaTime.getDeltaTime(freshAiRanTelemetry.timestamp) * 3));
            this.aiRanTelemetryChanges.emit(machineId);

          });
        }));
    }
  }

   private updateAiRan(aiRanTelemetryDtoToUpdate: AiRanTelemetryDto, freshAiRanTelemetryDto: AiRanTelemetryDto): void {
    aiRanTelemetryDtoToUpdate.status = freshAiRanTelemetryDto.status;
    aiRanTelemetryDtoToUpdate.timestamp = freshAiRanTelemetryDto.timestamp;
    aiRanTelemetryDtoToUpdate.ip = freshAiRanTelemetryDto.ip;
    aiRanTelemetryDtoToUpdate.cn5gStatus = freshAiRanTelemetryDto.cn5gStatus;
    aiRanTelemetryDtoToUpdate.fronthaulType = freshAiRanTelemetryDto.fronthaulType;
    aiRanTelemetryDtoToUpdate.flexric = freshAiRanTelemetryDto.flexric;
    aiRanTelemetryDtoToUpdate.gnbs = freshAiRanTelemetryDto.gnbs;
    aiRanTelemetryDtoToUpdate.nvCubb = freshAiRanTelemetryDto.nvCubb;
    aiRanTelemetryDtoToUpdate.usrp = freshAiRanTelemetryDto.usrp;
    aiRanTelemetryDtoToUpdate.ptp = freshAiRanTelemetryDto.ptp;
    aiRanTelemetryDtoToUpdate.usrp = freshAiRanTelemetryDto.usrp;
  }

  public getAiRanTelemetry(machineId: MachineId): AiRanTelemetry | undefined {
    const aiRanTelemetryOptional = this.aiRanTelemetries.get(machineId);
    if (aiRanTelemetryOptional) return this.aiRanTelemetryDtoToaiRanTelemetry(aiRanTelemetryOptional);
    return undefined;
  }

  public getAiRanTelemetry$(machineId: MachineId): Observable<AiRanTelemetry> {
    return new Observable(subscriber => {
      const oaiboxTelemetry = this.getAiRanTelemetry(machineId);
      if (oaiboxTelemetry) subscriber.next(oaiboxTelemetry);

      const subscription = this.aiRanTelemetryChanges
        .pipe(filter(changedMachineId => changedMachineId === machineId))
        .subscribe(changedMachineId => {
          const aiRanTelemetryOptional = this.getAiRanTelemetry(changedMachineId);
          if (aiRanTelemetryOptional) subscriber.next(aiRanTelemetryOptional);
        });
      return {unsubscribe: () => subscription.unsubscribe()};
    });
  }

  public getOaiboxStatusChanges$(): Observable<void> {
    return this.aiRanTelemetryChanges.pipe(map(() => {
      return;
    }));
  }

  private aiRanTelemetryDtoToaiRanTelemetry(aiRanTelemetryDto: AiRanTelemetryDto): AiRanTelemetry {
    return {
      status: aiRanTelemetryDto.status,
      cn5gStatus: aiRanTelemetryDto.cn5gStatus,
      fronthaulType: aiRanTelemetryDto.fronthaulType,
      ip: aiRanTelemetryDto.ip,
      timestamp: aiRanTelemetryDto.timestamp,
      gnbs: aiRanTelemetryDto.gnbs,
      nvCubb: aiRanTelemetryDto.nvCubb,
      flexric: aiRanTelemetryDto.flexric,
      usrp: aiRanTelemetryDto.usrp,
      ptp: aiRanTelemetryDto.ptp,
    }
  }

  public stopListeningAiRanTelemetry(machineId: MachineId): void {
    this.aiRanTelemetriesSubscriptions.get(machineId)?.unsubscribe();
    this.aiRanTelemetriesSubscriptions.delete(machineId);
    this.aiRanTelemetries.delete(machineId);

    clearTimeout(this.aiRanTelemetriesStatusTimeout.get(machineId));
    this.aiRanTelemetriesStatusTimeout.delete(machineId);
  }
}
